<template>
  <div class="bh-text-input--wrap" :class="{ noMargin: noMargin }">
    <label :for="id" v-if="label" class="bh-text-input__label">{{
      label
    }}</label>
    <div
      class="bh-text-input"
      :class="[
        roundedClass ? roundedClass : '',
        inputBackgroundClass && !isEditing ? inputBackgroundClass : '',
        inputEditingBackgroundClass && isEditing
          ? inputEditingBackgroundClass
          : '',
        error ? 'error' : '',
        condition ? '' : 'disabled',
        higher ? 'higher' : '',
      ]"
    >
      <slot name="prepend-inner" v-if="!disabled">
        <v-icon
          v-if="prependIcon"
          class="bh-text-input__icon bh-text-input__icon--prepend"
          color="accent"
        >
          {{ prependIcon }}
        </v-icon>
        <v-icon
          class="bh-text-input__icon bh-text-input__icon--editing"
          v-on:click="toggleEdit"
          color="accent"
          :class="
            !editButtonRight && showConfirmEdit && !isEditing
              ? 'd-inline'
              : 'd-none'
          "
        >
          bhi-pencil
        </v-icon>
      </slot>
      <span
        class="bh-text-input__input bh-text-input__input--readonly-span"
        v-if="isReadonly && readonlyAsSpan"
      >
        {{ showConfirmEdit && isEditing ? newValue : value }}
      </span>
      <input
        v-else
        :type="type"
        :inputmode="inputmode"
        class="bh-text-input__input"
        ref="input"
        :min="
          type.toLowerCase() == 'number' && minNumber !== false
            ? minNumber
            : false
        "
        :max="
          type.toLowerCase() == 'number' && maxNumber !== false
            ? maxNumber
            : false
        "
        :maxlength="
          type.toLowerCase() != 'number' && maxLength > 0 ? maxLength : false
        "
        :placeholder="placeholder"
        :id="id"
        :value="showConfirmEdit && isEditing ? newValue : value"
        :readonly="isReadonly"
        v-on:keyup="validateMethod($event.target.value, validationType)"
        v-on:keydown.enter="
          showConfirmEdit && isEditing && updateValue($event.target.value)
        "
        @blur="onBlur"
        @input="onInput"
      />
      <slot name="append-inner">
        <template v-if="showConfirmEdit && isEditing">
          <v-icon
            class="bh-text-input__icon bh-text-input__icon--save"
            v-on:click="updateValue($refs['input'].value)"
            color="accent"
          >
            bhi-check-circle-filled
          </v-icon>
          <v-icon
            class="bh-text-input__icon bh-text-input__icon--cancel"
            v-on:click="cleanUpValues"
            color="accent"
          >
            bhi-close-circle
          </v-icon>
        </template>
        <v-icon
          class="bh-text-input__icon bh-text-input__icon--editing"
          :class="
            editButtonRight && showConfirmEdit && !isEditing
              ? 'd-inline'
              : 'd-none'
          "
          v-on:click="toggleEdit"
          color="accent"
        >
          bhi-pencil
        </v-icon>
        <v-icon
          v-if="appendIcon"
          class="bh-text-input__icon bh-text-input__icon--append"
          color="accent"
        >
          {{ appendIcon }}
        </v-icon>
      </slot>
      <v-tooltip v-model="error" right>
        <template v-slot:activator="{ attrs }">
          <span class="" v-bind="attrs"></span>
        </template>
        <span v-if="newValue && validateMethod == 'maxlength'"
          >Too many characters</span
        >
        <span v-else-if="newValue">This field value is invalid</span>
        <span v-else>Value can not be empty</span>
      </v-tooltip>
    </div>

    <div class="d-flex">
      <div class="bh-text-input__rules flex-1 pr-4">
        <template v-if="isRuleInvalid">
          {{ rulesError }}
        </template>
      </div>
      <span
        class="bh-text-input__counter"
        v-if="showCounter"
        :class="currentCount && currentCount > counter ? 'tooLong' : ''"
        >{{ currentCount }}/{{ counter }}</span
      >
    </div>

    <v-dialog
      v-model="showConfirmationPopup"
      persistent
      max-width="290"
      @click:outside="onClickOutside"
      @keydown="onKeydown"
    >
      <v-card>
        <v-card-title class="headline">Change confirmation</v-card-title>
        <v-card-text>{{ this.confirmationPopup }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="rounded-pill" text @click="cancelChanges">Cancel</v-btn>
          <v-btn
            class="rounded-pill elevation-0"
            color="accent"
            @click="submitChanges"
            >Ok</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import { debounce } from '@/helpers/debounce'
import phoneFormat from '@/helpers/phoneFormat.js'
export default {
  name: 'BhTextInput',
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: [String, Number],
      default: '',
    },
    roundedClass: {
      type: String,
      default: 'rounded-sm',
    },
    id: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'text',
    },
    inputmode: {
      type: String,
      default: 'text',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    showConfirmEdit: {
      type: Boolean,
      default: false,
    },
    editButtonRight: {
      type: Boolean,
      default: false,
    },
    readonlyAsSpan: {
      type: Boolean,
      default: false,
    },
    appendIcon: {
      type: String,
      default: '',
    },
    prependIcon: {
      type: String,
      default: '',
    },
    inputBackgroundClass: {
      type: String,
      default: '',
    },
    inputEditingBackgroundClass: {
      type: String,
      default: '',
    },
    maxLength: {
      type: Number,
      default: 0,
    },
    maxNumber: {
      type: [Number, Boolean],
      default: false,
    },
    minNumber: {
      type: [Number, Boolean],
      default: false,
    },
    confirmationPopup: {
      type: [String, Boolean],
      default: false,
    },
    validate: {
      type: Boolean,
      default: false,
    },
    validationType: {
      type: String,
      default: 'required',
    },
    condition: {
      type: [Boolean, String],
      default: true,
    },
    higher: {
      type: Boolean,
      default: false,
    },
    noMargin: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    counter: {
      type: [Boolean, Number],
      default: false,
    },
    showCounterInRange: {
      type: [Number],
      default: 0,
    },
    rules: {
      type: Array,
      default: () => [],
    },
    forceRules: {
      type: Boolean,
      default: false,
    },
    editModeEnabled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    isEditing: false,
    showConfirmationPopup: false,
    newValue: '',
    error: false,
    inputChanged: false,
    countValue: '',
  }),
  computed: {
    isReadonly() {
      const { readonly, showConfirmEdit, isEditing } = this

      return readonly || (showConfirmEdit && !isEditing)
    },
    rulesError() {
      if (!this.rules || this.readonly || this.disabled) return false

      const currentValue =
        this.showConfirmEdit && this.isEditing ? this.newValue : this.value

      for (const rule of this.rules) {
        const ruleValid = rule(currentValue)
        if (ruleValid !== true) {
          return ruleValid
        }
      }

      return ''
    },
    isRuleInvalid() {
      return (
        this.rules &&
        this.rules.length > 0 &&
        this.rulesError &&
        (this.forceRules || this.inputChanged)
      )
    },
    currentCount() {
      return this.countValue ? this.countValue.length : 0
    },
    showCounter() {
      return (
        this.counter &&
        (this.showCounterInRange === 0 ||
          this.counter - this.showCounterInRange <= this.currentCount)
      )
    },
  },
  methods: {
    count(val) {
      // this.newValue = val
      this.countValue = val
    },
    onInput($event) {
      this.inputChanged = true

      if (!this.showConfirmEdit && !this.isReadonly) {
        this.count($event.target.value)
        this.$emit('input-text', $event.target.value)
      }
    },
    onBlur() {
      if (!this.isReadonly) {
        this.inputChanged = true
      }
    },
    focusInput() {
      this.$refs.input.focus()
    },
    submitChanges() {
      this.showConfirmationPopup = false
      this.updateValue(this.newValue, true)
    },
    editModeDisabled() {
      this.editModeEnabled = false
      this.$emit('update:editModeEnabled', false)
    },
    cancelChanges() {
      this.showConfirmationPopup = false
      this.isEditing = !this.isEditing
      this.editModeDisabled()
    },
    onClickOutside() {
      this.cancelChanges()
    },
    onKeydown(key) {
      if (key.code === 'Escape' && key.isTrusted) this.cancelChanges()
    },
    toggleEdit() {
      if (this.condition) {
        this.newValue = this.value
        this.isEditing = !this.isEditing
        this.error = false
        this.$emit('update:editModeEnabled', !this.editModeEnabled)
      }
    },
    updateValue(value, confirmed = false) {
      if (this.error) return
      if (this.confirmationPopup && !confirmed) {
        this.showConfirmationPopup = true
        this.newValue = value
        return
      }
      this.isEditing = !this.isEditing
      let newValue = value
      if (
        this.type.toLowerCase() == 'number' &&
        this.minNumber !== false &&
        this.maxNumber !== false
      ) {
        const maxNumber = parseInt(this.maxNumber, 10)
        const minNumber = parseInt(this.minNumber, 10)

        newValue = parseInt(newValue, 10)

        if (!isNaN(maxNumber) && newValue >= maxNumber) {
          newValue = maxNumber
          this.$refs.input.value = maxNumber
        } else if (!isNaN(minNumber) && newValue <= minNumber) {
          newValue = minNumber
          this.$refs.input.value = minNumber
        } else {
          this.$refs.input.value = parseInt(newValue, 10)
        }
      }
      if (this.type.toLowerCase() == 'email') {
        if (!this.validEmail(value)) {
          this.error = true
          return
        }
      }
      if (this.type.toLowerCase() == 'tel') {
        let x = this.phoneUsFormat(value)
        if (x) {
          newValue = x
        }
      }

      this.$emit('input', newValue)
      if (this.showConfirmEdit) {
        this.newValue = newValue
      }
    },
    validateMethod(value) {
      if (this.validate) {
        this.error = false
        this.newValue = value
        if (this.validationType == 'email') {
          debounce(() => this.validEmail(value), 1000, 'BhTextInput')
        } else if (this.validationType == 'maxlength') {
          if (this.newValue.length > this.counter) this.error = true
        }
      }
    },
    validEmail: function(email) {
      let re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      let valid = re.test(email)
      this.error = !valid
      return valid
    },
    cleanUpValues() {
      this.isEditing = !this.isEditing
      this.error = false
      this.countValue = this.value
      this.newValue = this.value
      this.editModeDisabled()
    },
    phoneUsFormat: phoneFormat.phoneUsFormat,
  },
  mounted() {
    this.newValue = this.value
    this.countValue = this.value
  },
  watch: {
    value(val) {
      this.newValue = val
    },
    newValue(val) {
      this.countValue = val
    },
  },
}
</script>
<style lang="scss">
.bh-text-input {
  width: 100%;
  height: 40px;
  overflow: hidden;
  display: flex;
  flex-wrap: nowrap;
  &.disabled {
    opacity: 0.3;
  }
  &.higher {
    height: 48px;
  }
  &.error {
    background-color: $bh-red !important;
    border-color: $bh-red !important;
  }
  &--wrap {
    margin-bottom: 12px;
    position: relative;

    &.noMargin {
      margin: 0;
    }
  }

  &__icon {
    &--save,
    &--cancel {
      margin-left: 0px;
      margin-right: 8px;
    }

    &--prepend {
      width: 22px;
    }
  }

  &__label {
    color: $bh-gray--2;
    font-weight: 700;
    font-size: 0.857rem;
    line-height: 1.6667;
  }

  &__rules {
    font-size: 0.8em;
    color: var(--bh-red--dark);
  }

  &__counter {
    font-size: 0.8em;
    align-self: flex-end;
    color: $bh-gray;
    &.tooLong {
      color: $bh-red--dark;
    }
  }

  &__input {
    flex: 1;
    padding: 0 12px;
    min-width: 0;
    font-weight: 400;
    line-height: 40px;
    outline: none;
    -moz-appearance: textfield;

    &--readonly-span {
      flex: initial;
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    &.v-input--is-readonly {
      .v-input__slot {
        background-color: $bh-white !important;
        padding-left: 0 !important;
        &::before {
          display: none;
        }
        & > .v-input {
          &__prepend-inner {
            width: 22px;
            margin-right: 16px;
          }

          &__append-inner {
            margin-left: 0;
          }
        }
      }
    }
  }
  &__icon {
    &.v-icon.v-icon::after {
      width: 31px;
      height: 31px;
      left: -7px;
      top: 4px;
      transform: scale(1);
    }
  }
}
</style>
